import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCompanyNotificationSettings,
  updateCompanyNotificationSettings,
} from '../../../../api/General/NotificationPreference';
import type { CompanyNotificationSettingView } from '../../../../api/General/NotificationPreference';
import NotificationPreferenceMatrix from '../../../../components/notifications/NotificationPreferenceMatrix';
import { Spinner } from '../../../../components/ui/Spinner';
import Button from '../../../../components/ui/Button';
import AppIcon from '../../../../components/AppIcon';
import { useNotificationModules } from '../../../../hooks/useNotificationModules';

const CompanyDefaultsTab: React.FC = () => {
  const { t } = useTranslation();
  // Every row the API returned. What is SHOWN (and saved) is `visibleSettings` below.
  const [settings, setSettings] = useState<CompanyNotificationSettingView[]>([]);
  // Review-3 finding 9 — a TEST company's must-send email (payslip, asset handover code, billing, inactivity)
  // is governed by the master switch AND these company rows, and the test-muting helpers write them muted.
  // So in a test company every row is shown, or a muted must-send row could never be turned back on.
  // Fresh: the test-company mark can change under a cached snapshot (review-3 finding 3).
  const { data: modulesView } = useNotificationModules(true, { fresh: true });
  const isTestCompany = modulesView?.isTestCompany === true;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCompanyNotificationSettings();
      if (result.status === 200 && result.payload) {
        // CO-375 / E13 — the API returns every type (test muting and the sandbox GET-then-PUT the list).
        // Filtering to what is shown happens in `visibleSettings`.
        setSettings(result.payload);
      } else {
        setError(result.errors?.[0]?.description || t('Failed to load settings'));
      }
    } catch (err) {
      setError(t('Failed to load settings'));
      console.error('Failed to load company notification settings:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Only Module-class types have a switch in a real company: must-send, platform and human-initiated types
  // are hidden there because a toggle that cannot act is not shown. In a TEST company the gate honours every
  // company row (no exemption at all), so every row is shown. Saving sends only the rows shown, so hidden
  // rows are never rewritten. `class` absent = a pre-E13 server: show all.
  const visibleSettings = useMemo(
    () => (isTestCompany ? settings : settings.filter((s) => !s.class || s.class === 'Module')),
    [settings, isTestCompany]
  );

  const handleSettingChange = useCallback(
    (notificationTypeId: number, field: string, value: boolean) => {
      setSettings((prev) =>
        prev.map((s) => {
          if (s.notificationTypeId !== notificationTypeId) return s;
          return { ...s, [field]: value };
        })
      );
      setHasChanges(true);
      setSaveSuccess(false);
    },
    []
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = visibleSettings.map((s) => ({
        notificationTypeId: s.notificationTypeId,
        emailsDisabled: s.emailsDisabled,
        mobilePushNotificationsDisabled: s.mobilePushNotificationsDisabled,
        inAppDisabled: s.inAppDisabled,
      }));
      const result = await updateCompanyNotificationSettings(payload);
      if (result.errors?.length) {
        setError(result.errors[0]?.description || t('Failed to save settings'));
        return;
      }
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save company notification settings:', err);
      setError(t('Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="company-defaults-tab" data-test-company={isTestCompany ? 'true' : 'false'}>
      {/* Description */}
      <div className="rounded-lg border border-border bg-muted/30 p-4">
        <div className="flex items-start gap-3">
          <AppIcon name="Info" size={18} className="text-primary mt-0.5 shrink-0" />
          <div className="text-sm text-muted-foreground">
            <p>
              {t('These settings control the default notification channels for all users in your company.')}
            </p>
            <p className="mt-1">
              {t('When a channel is disabled, users cannot enable it for that notification type.')}
            </p>
            {isTestCompany && (
              <p
                className="mt-2 inline-flex items-start gap-1.5 font-medium text-foreground"
                data-testid="e13-company-defaults-test-company-note"
              >
                <AppIcon name="FlaskConical" size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                {t('notificationModules.companyDefaults.testCompanyRows')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive flex items-center gap-2">
          <AppIcon name="AlertCircle" size={16} className="shrink-0" />
          {error}
        </div>
      )}

      {/* Matrix */}
      <NotificationPreferenceMatrix
        mode="admin"
        companySettings={visibleSettings}
        onCompanySettingChange={handleSettingChange}
      />

      {/* Save bar */}
      <div className="flex items-center justify-end gap-3 pt-2">
        {saveSuccess && (
          <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1.5">
            <AppIcon name="Check" size={16} />
            {t('Settings saved successfully')}
          </span>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={fetchSettings}
          disabled={saving}
        >
          {t('Reset')}
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          loading={saving}
        >
          {t('Save Changes')}
        </Button>
      </div>
    </div>
  );
};

export default CompanyDefaultsTab;
